import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Plus, IndianRupee, FileCheck2, FileX2, Wallet, CheckCircle2, PenTool } from "lucide-react";

const QUOTE_BADGE = {
  pending: "bg-[#F6EED8] text-[#6E5416]",
  approved: "bg-[#E1F0E6] text-[#0F5E39]",
  rejected: "bg-red-50 text-red-700",
};

export function CADQuotesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [orders, setOrders] = useState([]);
  const canEdit = can(user, "cad.quotes.edit");

  const load = async () => {
    try { const { data } = await api.get("/cad/quotes"); setRows(data); } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/orders", { params: { page: 1, page_size: 50 } }); setOrders(data.items || []); } catch {}
  })(); }, []);

  const decide = async (id, decision) => {
    try { await api.post(`/cad/quotes/${id}/decide?decision=${decision}`); toast.success(`Quote ${decision}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="cad-quotes-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Design</div>
          <h1 className="font-serif text-3xl lg:text-4xl font-medium">CAD Quotes</h1>
          <p className="text-[#4A5A50] mt-1">Cost paid to designer + 100% default markup = what we charge the customer.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setOpenNew(true)} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="new-cad-quote-btn">
            <Plus size={14} className="mr-1"/> New Quote
          </Button>
        )}
      </div>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-0 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F1EDDF] text-[10px] uppercase tracking-widest text-[#4A5A50]">
              <tr>
                <th className="px-4 py-3 text-left">Order</th>
                <th className="px-4 py-3 text-left">Designer</th>
                <th className="px-4 py-3 text-right">CAD cost</th>
                <th className="px-4 py-3 text-right">Markup</th>
                <th className="px-4 py-3 text-right">Customer price</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4DFCF]">
              {rows.map(q => (
                <tr key={q.id} className="hover:bg-[#FAF7EF]" data-testid={`cad-quote-row-${q.id}`}>
                  <td className="px-4 py-3 font-medium">{q.order_reference || "—"}</td>
                  <td className="px-4 py-3">{q.designer_name || "—"}</td>
                  <td className="px-4 py-3 text-right"><IndianRupee size={11} className="inline mr-0.5"/>{(q.cad_cost || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right">{q.markup_percent}%</td>
                  <td className="px-4 py-3 text-right font-medium text-[#6E5416]"><IndianRupee size={11} className="inline mr-0.5"/>{(q.customer_price || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${QUOTE_BADGE[q.status]}`}>{q.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {q.status === "pending" && canEdit && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => decide(q.id, "approved")} className="rounded-full" data-testid={`quote-approve-${q.id}`}>
                          <FileCheck2 size={14} className="mr-1"/> Approve
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => decide(q.id, "rejected")} className="text-red-600" data-testid={`quote-reject-${q.id}`}>
                          <FileX2 size={14} className="mr-1"/> Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-[#4A5A50]"><PenTool size={22} className="text-[#C9A24B] mx-auto mb-2"/>No CAD quotes yet</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {canEdit && (
        <NewQuoteDialog open={openNew} setOpen={setOpenNew} orders={orders} onCreated={load}/>
      )}
    </div>
  );
}

function NewQuoteDialog({ open, setOpen, orders, onCreated }) {
  const [form, setForm] = useState({ order_id: "", order_item_id: "", cad_cost: "", markup_percent: 100, designer_name: "", remarks: "" });
  const selectedOrder = useMemo(() => orders.find(o => o.id === form.order_id), [orders, form.order_id]);

  const submit = async () => {
    if (!form.order_item_id || !form.cad_cost) return toast.error("Order item & cost required");
    try {
      await api.post("/cad/quotes", {
        order_item_id: form.order_item_id,
        cad_cost: Number(form.cad_cost),
        markup_percent: Number(form.markup_percent) || 0,
        designer_name: form.designer_name,
        remarks: form.remarks,
      });
      toast.success("Quote created");
      setOpen(false); setForm({ order_id: "", order_item_id: "", cad_cost: "", markup_percent: 100, designer_name: "", remarks: "" });
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const preview = form.cad_cost ? Number(form.cad_cost) * (1 + (Number(form.markup_percent) || 0) / 100) : 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="new-quote-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New CAD Quote</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Order</Label>
            <Select value={form.order_id} onValueChange={(v)=>setForm({...form, order_id:v, order_item_id:""})}>
              <SelectTrigger className="mt-1" data-testid="quote-order-select"><SelectValue placeholder="Select order"/></SelectTrigger>
              <SelectContent>{orders.map(o => <SelectItem key={o.id} value={o.id}>{o.reference_number} · {o.client_name || "No client"}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {selectedOrder && (
            <div><Label>Item</Label>
              <Select value={form.order_item_id} onValueChange={(v)=>setForm({...form, order_item_id:v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select item"/></SelectTrigger>
                <SelectContent>
                  {(selectedOrder.items || []).map((it, i) => (
                    <SelectItem key={it.id} value={it.id}>#{i+1} · {it.product_category_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>CAD cost (₹)</Label><Input type="number" value={form.cad_cost} onChange={(e)=>setForm({...form, cad_cost:e.target.value})} className="mt-1" data-testid="quote-cost"/></div>
            <div><Label>Markup (%)</Label><Input type="number" value={form.markup_percent} onChange={(e)=>setForm({...form, markup_percent:e.target.value})} className="mt-1"/></div>
          </div>
          <div><Label>Designer</Label><Input value={form.designer_name} onChange={(e)=>setForm({...form, designer_name:e.target.value})} className="mt-1"/></div>
          <div><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e)=>setForm({...form, remarks:e.target.value})} className="mt-1"/></div>
          <div className="p-3 bg-[#F6EED8] rounded-lg text-sm">
            Customer will be charged: <span className="font-medium text-[#6E5416]">₹{preview.toLocaleString('en-IN')}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="quote-submit">Create Quote</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ CAD FILES / PAYABLES ============
export function CADFilesPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], totals_by_designer: {} });
  const [tab, setTab] = useState("all");
  const canPay = can(user, "cad.files.pay");
  const canEdit = can(user, "cad.files.edit");

  const load = async () => {
    const params = {};
    if (tab === "unpaid") params.is_paid = false;
    if (tab === "paid") params.is_paid = true;
    const { data } = await api.get("/cad/files", { params });
    setData(data);
  };
  useEffect(() => { load(); }, [tab]);

  const pay = async (id) => {
    try { await api.post(`/cad/files/${id}/pay`); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const totalUnpaid = Object.values(data.totals_by_designer || {}).reduce((a, t) => a + (t.unpaid || 0), 0);

  return (
    <div className="space-y-6" data-testid="cad-files-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Design ledger</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">CAD Files & Payables</h1>
        <p className="text-[#4A5A50] mt-1">Track designer files, our cost paid, and outstanding payables.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Total files</div>
            <div className="font-serif text-3xl mt-1">{data.items?.length || 0}</div>
          </CardContent>
        </Card>
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Outstanding payables</div>
            <div className="font-serif text-3xl mt-1 text-red-700"><IndianRupee size={16} className="inline mr-0.5"/>{totalUnpaid.toLocaleString('en-IN')}</div>
          </CardContent>
        </Card>
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Designers</div>
            <div className="font-serif text-3xl mt-1">{Object.keys(data.totals_by_designer || {}).length}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F1EDDF]">
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="unpaid" data-testid="tab-unpaid">Unpaid</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">Paid</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          <Card className="border-[#E4DFCF] rounded-xl mt-3">
            <CardContent className="p-0 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#F1EDDF] text-[10px] uppercase tracking-widest text-[#4A5A50]">
                  <tr>
                    <th className="px-4 py-3 text-left">Order</th>
                    <th className="px-4 py-3 text-left">Designer</th>
                    <th className="px-4 py-3 text-right">Cost paid</th>
                    <th className="px-4 py-3 text-right">Charged to customer</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Payment</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E4DFCF]">
                  {(data.items || []).map(r => (
                    <tr key={r.id} className="hover:bg-[#FAF7EF]" data-testid={`cad-file-row-${r.id}`}>
                      <td className="px-4 py-3 font-medium">{r.order_reference || "—"}</td>
                      <td className="px-4 py-3">{r.designer_name}</td>
                      <td className="px-4 py-3 text-right"><IndianRupee size={11} className="inline mr-0.5"/>{(r.cost_paid || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-right"><IndianRupee size={11} className="inline mr-0.5"/>{(r.amount_charged_to_customer || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#F1EDDF]">{r.status}</span></td>
                      <td className="px-4 py-3">
                        {r.is_paid ? (
                          <span className="text-[10px] uppercase tracking-widest text-[#0F5E39] bg-[#E1F0E6] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><CheckCircle2 size={10}/> Paid {r.paid_at ? format(new Date(r.paid_at), "dd MMM") : ""}</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest text-red-700 bg-red-50 px-2 py-0.5 rounded-full">Unpaid</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!r.is_paid && canPay && (
                          <Button size="sm" onClick={() => pay(r.id)} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid={`file-pay-${r.id}`}>
                            <Wallet size={14} className="mr-1"/> Mark paid
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(data.items || []).length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-[#4A5A50]">No files</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Per-designer totals */}
      {Object.keys(data.totals_by_designer || {}).length > 0 && (
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-3">Per-designer</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(data.totals_by_designer).map(([d, t]) => (
                <div key={d} className="border border-[#E4DFCF] rounded-lg p-3" data-testid={`designer-tile-${d}`}>
                  <div className="font-serif text-lg">{d}</div>
                  <div className="text-xs text-[#4A5A50] mt-0.5">{t.count} files</div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-[#4A5A50]">Total cost</span>
                    <span className="font-medium">₹{(t.total_cost || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-[#4A5A50]">Unpaid</span>
                    <span className="font-medium text-red-700">₹{(t.unpaid || 0).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
